import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion'; // Keep this line commented out or remove it
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';

const Services = () => {
  return (
    <div className="services-page pt-24 pb-16">
      <SEO 
        title="Drone Services in Lehigh Valley, PA | Aerial, FPV & More | Cosmik Drones"
        description="Explore expert drone services by Cosmik Drones in Lehigh Valley: aerial photography, FPV filming, real estate, events & more. FAA Certified. Lehigh Valley Chamber Member."
        canonicalUrl="https://cosmikdrones.com/services"
      />
      
      <SchemaMarkup 
        type="Service"
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Drone Photography and Videography Services",
          "provider": {
            "@type": "LocalBusiness",
            "name": "Cosmik Drones",
            "url": "https://cosmikdrones.com",
            "logo": "https://cosmikdrones.com/Cosmik%20Logo/Kolonia_Logo.png",
            "image": "https://cosmikdrones.com/Cosmik%20Logo/Kolonia_Logo.png",
            "telephone": "610-509-0867",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Bethlehem",
              "addressRegion": "PA",
              "addressCountry": "US"
            },
            "memberOf": {
              "@type": "Organization",
              "name": "Greater Lehigh Valley Chamber of Commerce",
              "url": "https://www.lehighvalleychamber.org/"
            },
            "sameAs": [
              "https://www.instagram.com/josh.kolonia/",
              "https://www.facebook.com/josh.kolonia",
              "https://www.youtube.com/channel/cosmikdrones",
              "https://www.linkedin.com/company/cosmik-drones",
              "https://web.lehighvalleychamber.org/Video-Production-and-Drone-Services/Cosmik-Drones-17765"
            ]
          },
          "areaServed": {
            "@type": "AdministrativeArea",
            "name": "Lehigh Valley, Pennsylvania"
          },
          "description": "Professional drone services including aerial photography, FPV drone filming, cinematic videography, real estate visuals, construction monitoring, and event coverage in the Lehigh Valley and Eastern Pennsylvania.",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Drone Services Menu",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Aerial Photography" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Cinematic Videography" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "FPV Drone Filming" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Real Estate & Property Drone Services" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Construction & Mapping Drone Services" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Events & Sports Drone Coverage" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Live Drone Broadcasting" } }
            ]
          }
        }}
      />
      
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-lightText mb-6">My Services</h1>
          <p className="text-xl text-lightText/80 max-w-4xl mx-auto">
            Professional drone services in Lehigh Valley, Pennsylvania and beyond. FAA-certified, state-of-the-art equipment, and creative expertise.
          </p>
        </motion.div>

        {/* Core Services Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          <ServiceCard
            title="Aerial Photography"
            description="Breathtaking high-resolution aerial images that showcase your property, event, or landscape from unique perspectives."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
            delay={0}
          />
          <ServiceCard
            title="Cinematic Videography"
            description="Dynamic aerial video footage that tells your story with smooth, professional cinematography and stunning 4K resolution."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            }
            delay={0.1}
          />
          <ServiceCard
            title="FPV Drone Filming"
            description="Immersive first-person-view drone filming for dynamic, action-packed sequences that put viewers right in the middle of the action."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
              </svg>
            }
            delay={0.2}
          />
          <ServiceCard
            title="Real Estate & Property"
            description="Showcase properties with compelling aerial views that highlight features, location advantages, and neighborhood context."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            }
            delay={0.3}
          />
          <ServiceCard
            title="Construction & Mapping"
            description="Progress documentation, site surveys, and 3D mapping services for construction and development projects."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            }
            delay={0.4}
          />
          <ServiceCard
            title="Events & Sports"
            description="Dynamic coverage of events, sports competitions, and festivals that capture the energy, scale, and excitement from above."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
            }
            delay={0.5}
          />
        </div>

        {/* Eagle Statue Image - Decorative */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="group relative rounded-lg overflow-hidden shadow-lg">
            <img 
              src="/images/Drone/Close Statue Eagle.jpg" 
              alt="Eagle Monument captured by drone" 
              className="w-full h-[500px] object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-darkBg to-transparent opacity-50"></div>
          </div>
        </motion.div>

        {/* Featured Service: FPV Drones */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="glass-card p-10 rounded-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-3xl font-bold text-lightText mb-6">FPV Drone Cinematography</h2>
                <p className="text-lg text-lightText/80 mb-6">
                  My specialty is First Person View (FPV) drone flying, which offers a completely different perspective from traditional drone footage. FPV drones can:
                </p>
                <ul className="space-y-3 text-lightText/80 mb-6">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Navigate through tight spaces, including interiors</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Achieve extreme speeds over 100 MPH</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Create dynamic, immersive footage with fluid movements</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Capture perspectives impossible with traditional drones</span>
                  </li>
                </ul>
                <div>
                  <a 
                    href="/contact" 
                    className="bg-primary hover:bg-primary/90 text-darkBg font-bold py-3 px-8 rounded-md text-lg transition-colors inline-block"
                  >
                    Book FPV Services
                  </a>
                </div>
              </div>
              <div className="order-1 lg:order-2 relative h-[350px] rounded-lg overflow-hidden">
                <video 
                  src="/videos/scenery website quick vid 1.mp4" 
                  alt="FPV Drone in action" 
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                ></video>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Sports & Racing Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="glass-card p-10 rounded-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-lightText mb-6">Sports & Racing Coverage</h2>
                <p className="text-lg text-lightText/80 mb-6">
                  Capture the excitement and speed of motorsports, races, and athletic events with dynamic aerial footage that puts viewers right in the action.
                </p>
                <ul className="space-y-3 text-lightText/80 mb-6">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>High-speed tracking shots that follow the action</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Dramatic reveals and establishing shots of venues</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>FPV drone filming for immersive sports coverage</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Promotional content for events and sports organizations</span>
                  </li>
                </ul>
                <div>
                  <a 
                    href="/contact" 
                    className="bg-primary hover:bg-primary/90 text-darkBg font-bold py-3 px-8 rounded-md text-lg transition-colors inline-block"
                  >
                    Book Sports Coverage
                  </a>
                </div>
              </div>
              <div className="relative h-[350px] rounded-lg overflow-hidden">
                <video 
                  src="/videos/Drift video for website.mp4" 
                  alt="Drift Racing Drone Coverage" 
                  className="absolute inset-0 w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Live Broadcasting Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="glass-card p-10 rounded-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-3xl font-bold text-lightText mb-6">Live Drone Broadcasting</h2>
                <p className="text-lg text-lightText/80 mb-6">
                  Experience real-time aerial perspectives with my professional live broadcasting services. I can stream high-quality drone footage directly to your authorized devices or platforms.
                </p>
                <ul className="space-y-3 text-lightText/80 mb-6">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Real-time streaming to multiple devices via WiFi</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Live feed integration with event displays</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Secure, private streams for sensitive operations</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Perfect for events, sports, and construction monitoring</span>
                  </li>
                </ul>
                <div>
                  <a 
                    href="/contact" 
                    className="bg-primary hover:bg-primary/90 text-darkBg font-bold py-3 px-8 rounded-md text-lg transition-colors inline-block"
                  >
                    Book Live Broadcasting
                  </a>
                </div>
              </div>
              <div className="order-1 lg:order-2 relative h-[350px] rounded-lg overflow-hidden">
                <video 
                  src="/videos/Royals website video - SOUND.mp4" 
                  alt="Live Drone Broadcasting" 
                  className="absolute inset-0 w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAA Certification Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="glass-card p-10 rounded-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-lightText mb-6">FAA Certified</h2>
                <p className="text-lg text-lightText/80 mb-6">
                  All my drone operations are fully compliant with FAA regulations. I hold Part 107 Remote Pilot Certification and maintain comprehensive insurance coverage.
                </p>
                <ul className="space-y-3 text-lightText/80 mb-6">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Part 107 certified pilot</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Comprehensive liability insurance</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Airspace authorization capabilities</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Safety-first approach to all operations</span>
                  </li>
                </ul>
              </div>
              <div className="flex justify-center">
                <img 
                  src="/images/FAA License.JPG" 
                  alt="FAA Drone Certification" 
                  className="rounded-lg max-w-full shadow-lg"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Service Industries */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="text-3xl font-bold text-lightText mb-10 text-center">Industries I Serve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <IndustryCard 
              title="Real Estate" 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              }
            />
            <IndustryCard 
              title="Construction" 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            <IndustryCard 
              title="Sports & Events" 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                </svg>
              }
            />
            <IndustryCard 
              title="Film Production" 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                </svg>
              }
            />
            <IndustryCard 
              title="Weddings & Events" 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
                </svg>
              }
            />
            <IndustryCard 
              title="Tourism & Travel" 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            <IndustryCard 
              title="Agriculture" 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              }
            />
            <IndustryCard 
              title="Insurance" 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              }
            />
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-lightText mb-10 text-center">Frequently Asked Questions</h2>
          <div className="glass-card p-8 rounded-lg space-y-6">
            <div>
              <h3 className="text-xl font-bold text-lightText mb-2">What areas do you serve?</h3>
              <p className="text-lightText/80">
                We're based in Lehigh Valley, Pennsylvania but serve clients throughout the Northeast US and can travel for projects nationwide.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-lightText mb-2">How far in advance should I book?</h3>
              <p className="text-lightText/80">
                We recommend booking at least 2-3 weeks in advance for standard projects, and 4-6 weeks for complex or event-based shoots.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-lightText mb-2">What happens if the weather is bad?</h3>
              <p className="text-lightText/80">
                We closely monitor weather conditions and will work with you to reschedule if conditions aren't suitable for safe and effective flying.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-lightText mb-2">What's the turnaround time for edited footage?</h3>
              <p className="text-lightText/80">
                Standard projects are delivered within 1-2 weeks. Rush services are available for time-sensitive needs.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-lightText mb-2">Do you offer raw footage?</h3>
              <p className="text-lightText/80">
                Yes, we can provide raw footage as part of our packages or as an additional service upon request.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Detailed Services */}
        <div className="mb-24">
          <h2 className="text-3xl font-bold text-lightText mb-10 text-center">Detailed Services</h2>
          
          <div className="space-y-16">
            <DetailedService
              title="Real Estate & Architectural Photography"
              description="Showcase your properties with stunning aerial views that highlight features, surroundings, and amenities. Our drone photography services help real estate agents, property managers, and architects market their properties more effectively."
              image="/images/patio+(1+of+1).jpg"
              features={[
                "Property overviews and aerial context shots",
                "Interior-to-exterior reveal sequences",
                "Neighborhood and surrounding area footage",
                "Twilight/sunset photography for dramatic effect",
                "Integration with virtual tours and 3D mapping"
              ]}
              reversed={false}
            />
            
            <DetailedService
              title="Construction & Development"
              description="Document your construction projects from groundbreaking to completion with regular aerial progress updates. Our services help project managers, developers, and construction companies track and showcase their work."
              image="/images/0006.jpg"
              features={[
                "Site surveys and planning assistance",
                "Construction progress documentation",
                "Marketing materials for developments",
                "Inspection and monitoring of large structures",
                "Before and after comparisons"
              ]}
              reversed={true}
            />
            
            <DetailedService
              title="Sports & Event Coverage"
              description="Capture the energy and scale of your sporting events, festivals, and gatherings with dynamic aerial footage. Our event coverage helps organizers, promoters, and marketers create compelling content."
              image="/images/Phantoms TB toss.jpg"
              features={[
                "Dynamic FPV racing shots for sports events",
                "Crowd and venue establishing shots",
                "Follow shots of sporting action",
                "Live event broadcasting capability",
                "Same-day edited highlights for social media"
              ]}
              reversed={false}
            />

            <DetailedService
              title="Commercial Photography"
              description="Elevate your brand with cinematic aerial footage for commercials, promotional videos, and marketing campaigns. Our commercial work helps businesses showcase their locations, products, and services."
              image="/images/vinyard+x.jpg"
              features={[
                "TV and commercial advertisement shots",
                "Corporate facility showcases",
                "Product launch coverage",
                "Social media marketing content",
                "Brand storytelling from unique perspectives"
              ]}
              reversed={true}
            />

            <DetailedService
              title="Architectural & Historical Site Photography"
              description="Capturing the unique character of architectural marvels and historical landmarks. My services provide detailed imagery for preservation, documentation, or artistic representation."
              image="/images/Prison.jpg"
              features={[
                "Documentation of heritage sites",
                "Detailed structural imagery",
                "Artistic architectural representations",
                "Support for preservation projects",
                "Promotional material for historical tourism"
              ]}
              reversed={false}
            />

            <DetailedService
              title="Recreational & Action Event Coverage"
              description="Specializing in capturing dynamic, fast-paced recreational activities and action-filled events. From paintball tournaments to outdoor adventures, I bring the excitement to life."
              image="/images/Skirmish paintball photo website.jpg"
              features={[
                "FPV footage for immersive action sequences",
                "Coverage of tournaments and competitions",
                "Promotional content for adventure sports",
                "Capturing the energy of outdoor festivals",
                "Unique perspectives on recreational events"
              ]}
              reversed={true}
            />

            <DetailedService
              title="Scenic & Landscape Aerials"
              description="Showcasing the breathtaking beauty of natural landscapes, scenic vistas, and environmental features through high-quality aerial photography and videography."
              image="/images/winter photo 2.jpg"
              features={[
                "Tourism and travel promotion imagery",
                "Environmental documentation and surveys",
                "Artistic landscape prints and visuals",
                "Capturing seasonal beauty and changes",
                "Footage for nature documentaries and features"
              ]}
              reversed={false}
            />

            <DetailedService
              title="Product & Lifestyle Photography (Aerial Focus)"
              description="Creating compelling aerial and ground-level visuals for products in lifestyle settings, or for businesses like wineries, resorts, and unique event venues."
              image="/images/Wine.jpg"
              features={[
                "Aerial shots of vineyards, resorts, and estates",
                "Product placement in scenic environments",
                "Content for luxury brand marketing",
                "Promotional material for tourism and hospitality",
                "Lifestyle imagery for social media campaigns"
              ]}
              reversed={true}
            />

          </div>
        </div>
      </div>
    </div>
  );
};

// Service Card Component
const ServiceCard = ({ title, description, icon, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
    viewport={{ once: true }}
    className="glass-card p-8 rounded-lg flex flex-col items-center text-center h-full"
  >
    <div className="text-primary mb-6">{icon}</div>
    <h3 className="text-xl font-bold text-lightText mb-4">{title}</h3>
    <p className="text-lightText/80">{description}</p>
  </motion.div>
);

// Industry Card Component
const IndustryCard = ({ title, icon }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="glass-card p-6 rounded-lg text-center"
  >
    <div className="bg-primary/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
      {icon}
    </div>
    <h3 className="text-xl font-bold text-lightText">{title}</h3>
  </motion.div>
);

const DetailedService = ({ title, description, image, features, reversed, isVideo = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
    className="glass-card p-8 rounded-lg overflow-hidden"
  >
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${reversed ? 'lg:grid-flow-col lg:grid-cols-2' : ''}`}>
      <div className={reversed ? 'order-1 lg:order-2' : 'order-2 lg:order-1'}>
        <h3 className="text-2xl font-bold text-lightText mb-4">{title}</h3>
        <p className="text-lightText/80 mb-6">{description}</p>
        <ul className="space-y-3 text-lightText/80">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={`${reversed ? 'order-2 lg:order-1' : 'order-1 lg:order-2'} relative h-[300px] rounded-lg overflow-hidden`}>
        {isVideo ? (
          <video 
            src={image} 
            alt={title} 
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <img 
            src={image} 
            alt={title} 
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
      </div>
    </div>
  </motion.div>
);

export default Services; 